<script setup lang="ts">
import { useWordProcessorStore } from "~/components/Base/WordProcessor/useWordProcessorStore";

const documentId = inject("documentId") as any;
const documentSelfAssessmentStore = useDocumentSelfAssessmentStore(documentId);
const {
  activeStep,
  activeStepIndex,
  quillDelta,
  bodyParagraphs,
  stepParagraphPositions,
  stepExpectedInstances,
} = storeToRefs(documentSelfAssessmentStore);

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isCompanionMode } = storeToRefs(documentViewModeStore);

const documentChecklistStore = useDocumentChecklistStore(documentId);
const { checklistLevelId, checklistUnitNumber } = storeToRefs(
  documentChecklistStore
);

const documentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument } = storeToRefs(documentStore);
const { access: writingToolsAccess } =
  useStudentWritingToolsAccess(documentId);

const wordProcessorStore = useWordProcessorStore(documentId);

const studentInstructions = computed(() => {
  let levelId = checklistLevelId.value;
  let unitNumber = checklistUnitNumber.value;

  if (activeStep.value == undefined) return undefined;

  if (activeStep.value.instructionOverides != undefined) {
    const override = activeStep.value.instructionOverides.find((o) => {
      let matchesLevel = true;

      if (
        checklistLevelId.value != undefined &&
        (o.levelIds ?? []).length > 0
      ) {
        matchesLevel = (o.levelIds ?? []).includes(checklistLevelId.value);
      }

      let matchesUnit = true;

      if (
        checklistUnitNumber.value != undefined &&
        (o.unitNumbers ?? []).length > 0
      ) {
        matchesUnit = (o.unitNumbers ?? []).includes(checklistUnitNumber.value);
      }

      return matchesLevel && matchesUnit;
    });

    if (override != undefined) {
      return override.instructions;
    }
  }

  return activeStep.value?.instructions;
});

onMounted(() => {
  initialize();
});

watch(activeStepIndex, () => {
  initialize();
});

const initialize = () => {
  //  Here 'were going to auto apply'

  if (activeStep.value == undefined) return;

  useAutoApplyInstances({
    documentId,
    step: activeStep.value,
    editorRef: wordProcessorStore.editorRef,
    quillDelta: quillDelta.value,
  });
};

const markAsComplete = async () => {
  if (activeStep.value == undefined) return;

  const results = await documentSelfAssessmentStore.assessStep(
    activeStep.value
  );

  if (results != true) {
    return;
  }

  var nextIndex = (activeStepIndex.value ?? 0) + 1;

  documentSelfAssessmentStore.setActiveStepIndex(nextIndex);
};

const isNavigatingBackToChecklist = ref(false);
const backToChecklist = async () => {
  isNavigatingBackToChecklist.value = true;
  useSubmittedDocumentStore(documentId).updateDocumentState(
    DocumentState.editing
  );
  await useSubmittedDocumentStore(documentId).saveDocumentDebounced();
};
</script>

<template>
  <div class="flex flex-col gap-2" v-auto-animate>
    <div
      class="flex flex-row items-center gap-2 border-b pb-1 pl-4 pr-2 pt-4 justify-between"
    >
      <h2 class="text-h5 font-bold">Initial Review</h2>
      <BaseTextButton
        v-if="
          submittedDocument?.enableStudentSubmissionCreation == true ||
          writingToolsAccess.canWrite == true ||
          isCompanionMode == true
        "
        @click="backToChecklist"
        :show-spinner="isNavigatingBackToChecklist"
      >
        <div class="flex flex-row items-center gap-1 text-body-2">
          <BaseIconListChecks />
          Back to Checklist
        </div>
      </BaseTextButton>
    </div>
    <div v-if="activeStep?.errorMessage != undefined" class="px-4">
      <BaseLabelBadge color="danger">
        {{ activeStep?.errorMessage }}
      </BaseLabelBadge>
    </div>
    <div class="flex flex-col px-4 gap-2 pre-check-wrapper">
      <BaseLabelBadge class="">
        <BaseTextEditor
          :model-value="studentInstructions"
          :read-only="true"
          :key="activeStepIndex"
        />
      </BaseLabelBadge>
      <div class="flex flex-row items-center justify-end">
        <BaseButton @click="markAsComplete"> I have done this. </BaseButton>
      </div>
    </div>
  </div>
</template>

<style>
.pre-check-wrapper .ql-editor {
  font-size: 16px !important;
}
</style>
